import { isObject } from '@websolutespa/bom-core';
import { v4 as uuid } from 'uuid';
import { isChunkString, isChunkText, isChunkType, LlmChunk, LlmChunkError, LlmChunkHeader, LlmChunkItem, LlmChunkKnownItem, LlmChunkUnknownItem, LlmCredentials, LlmDecodedMessage, LlmDecodedThread, LlmMessage, LlmTest, LlmThread, StreamRequest, StreamResponse } from '../types';
import { getMockChunks, getMockPrompt } from './mock';
import { LlmState, LlmUploaded } from './useLlm';

export type MessageServiceRequest = StreamRequest;

export type MessageServiceResponse = {
};

export type MessageServiceOptions = LlmCredentials & {
  endpoint: string | undefined;
  locale: string;
  test?: LlmTest;
};

export class MessageDecoder {

  private decoder_ = new TextDecoder('utf-8');

  private lastUnparsedText_: string = '';
  set lastUnparsedText(lastUnparsedText: string) {
    // console.log('MessageService.lastUnparsedText.set', lastUnparsedText);
    this.lastUnparsedText_ = lastUnparsedText;
  }
  get lastUnparsedText(): string {
    return this.lastUnparsedText_;
  }

  constructor() {
  }

  bytesToChunks(byteArray: Uint8Array): LlmChunk[] {
    let chunks: LlmChunk[] = [];
    let text: string = '';
    try {
      text = this.decoder_.decode(byteArray);
      chunks = this.textToChunks(text);
    } catch (error) {
      console.log('decodedChunk.error', error, text);
    }
    // console.log(chunks, text);
    return chunks;
  }

  textToChunks(text: string): LlmChunk[] {
    let chunks: LlmChunk[] = [];
    let jsonText: string = '';
    try {
      if (text.match(/(",|},|],)$/)) {
        let textToReplace = this.lastUnparsedText + text;
        if (textToReplace.match(/^(,)/)) {
          console.log('textToChunks.error', `incorrect line starting [${text}]`);
          textToReplace = textToReplace.substring(1);
        }
        const replacedText = textToReplace.replace(/(,$)/, '');
        jsonText = `[${replacedText}]`;
        const jsonChunks: LlmChunk[] = JSON.parse(jsonText);
        chunks = jsonChunks;
        this.lastUnparsedText = '';
      } else {
        console.log('textToChunks.error', `incorrect line ending [${text}]`);
        this.lastUnparsedText += text;
      }
    } catch (error) {
      console.log('textToChunks.error', error);
      this.lastUnparsedText += text;
    }
    return chunks;
  }

  decodeChunk(chunk: LlmChunkItem): LlmChunkItem {
    if (isChunkString(chunk)) {
      return {
        type: 'text',
        text: chunk.content,
      };
    } else {
      return chunk;
    }
  }

  decodeMessages(messages: LlmMessage[]): LlmDecodedMessage[] {
    return messages.map(x => {
      if (Array.isArray(x.content)) {
        /*
        if (x.role === 'user') {
          return ({
            ...x,
            chunks: x.content.map(x => this.decodeChunk(x)),
          });
        }
        */
        return ({
          ...x,
          chunks: x.content.map(x => this.decodeChunk(x)),
        });
      } else if (x.content.lastIndexOf(',') === x.content.length - 1) {
        this.lastUnparsedText = '';
        const message = ({
          ...x,
          chunks: this.chunksToChunkItems(this.textToChunks(x.content)),
        });
        if (message.chunks.length === 0) {
          const chunk = {
            type: 'text' as const,
            text: x.content,
          };
          return ({
            ...x,
            chunks: [chunk],
            content: [chunk],
          });
        }
        return message;
      } else {
        const chunk = {
          type: 'text' as const,
          text: x.content,
        };
        return ({
          ...x,
          chunks: [chunk],
          content: [chunk],
        });
      }
    });
  }

  chunksToResponse(chunks: LlmChunk[]): StreamResponse {
    const response: StreamResponse = {
      chunks: [],
      threadId: '',
      messageId: '',
    };
    response.chunks = this.chunksToChunkItems(chunks, response);
    return response;
  }

  logChunks(chunks: LlmChunk[]) {
    chunks.forEach(x => {
      if (isObject(x)) {
        switch (x.type) {
          case 'error':
            console.error('LlmMessageService.error', x);
            break;
          case 'log':
            console.log('LlmMessageService.log', x);
            break;
          case 'header':
            console.log('LlmMessageService.header', x);
            break;
          case 'info':
            console.log('LlmMessageService.info', x);
            break;
          case 'end':
            console.log('LlmMessageService.end', x);
            break;
          default:
        }
      }
    });
  }

  chunksToChunkItems(chunks: LlmChunk[], response?: StreamResponse): LlmChunkItem[] {
    const chunkItems: LlmChunkItem[] = [];
    chunks.forEach(x => {
      let lastMessage = chunkItems[chunkItems.length - 1];
      if (isObject(x)) {
        // unknown chunk items (Record<string, unknown>) prevent the narrowing on type: switch on the known items
        const item = x as LlmChunkKnownItem;
        switch (item.type) {
          case 'log':
            break;
          case 'error':
            chunkItems.push(item);
            break;
          case 'header': {
            const { type, ...rest } = item;
            if (response) {
              Object.assign(response, rest);
            }
            // console.log('header', response);
          }
            break;
          case 'info': {
            if (response) {
              // !!! deprecated
              response.threadId = item.threadId || response.threadId;
            }
          }
            break;
          case 'end':
            break;
          case 'action':
          case 'actionItem':
            if (isChunkType(lastMessage, 'actionGroup')) {
              lastMessage.items.push(item);
            } else {
              chunkItems.push({
                type: 'actionGroup',
                items: [item],
              });
            }
            break;
          case 'cardItem':
            if (isChunkType(lastMessage, 'cardGroup')) {
              lastMessage.items.push(item);
            } else {
              chunkItems.push({
                type: 'cardGroup',
                items: [item],
              });
            }
            break;
          case 'productItem':
            if (isChunkType(lastMessage, 'productGroup')) {
              lastMessage.items.push(item);
            } else {
              chunkItems.push({
                type: 'productGroup',
                items: [item],
              });
            }
            break;
          case 'eventItem':
            if (isChunkType(lastMessage, 'eventGroup')) {
              lastMessage.items.push(item);
            } else {
              chunkItems.push({
                type: 'eventGroup',
                items: [item],
              });
            }
            break;
          case 'poiItem':
            if (isChunkType(lastMessage, 'poiGroup')) {
              lastMessage.items.push(item);
            } else {
              chunkItems.push({
                type: 'poiGroup',
                items: [item],
              });
            }
            break;
          case 'tripadvisor':
          case 'tripadvisorItem':
            if (isChunkType(lastMessage, 'tripadvisorGroup')) {
              lastMessage.items.push({
                ...item,
                type: 'tripadvisorItem',
              });
            } else {
              chunkItems.push({
                type: 'tripadvisorGroup',
                items: [{
                  ...item,
                  type: 'tripadvisorItem',
                }],
              });
            }
            break;
          default:
            if (
              typeof x.type === 'string' &&
              x.type.match(/Item$/)
            ) {
              const baseName = x.type.replace('Item', '');
              const groupName = `${baseName}Group`;
              if (lastMessage && lastMessage.type === groupName) {
                (lastMessage as { items: (typeof x)[] }).items.push(x);
              } else {
                chunkItems.push({
                  type: groupName,
                  items: [{
                    ...x,
                    type: baseName,
                  }],
                } as unknown as LlmChunkUnknownItem);
              }
            } else {
              chunkItems.push(x);
            }
        }
      } else if (typeof x === 'string') {
        // console.warn('chunksToChunkItems typeof string');
        if (!lastMessage) {
          lastMessage = {
            type: 'text',
            text: '',
          };
          chunkItems.push(lastMessage);
        }
        if (isChunkText(lastMessage)) {
          const lastChunkText = lastMessage;
          if (chunkItems.indexOf(lastChunkText) > 0) {
            // secondary chunk
            lastChunkText.text += x;
          } else if (!lastChunkText.text.includes('\n')) {
            // uncompleted first chunk
            if (x.includes('\n')) {
              const lines = x.split('\n');
              lines.forEach((line, i) => {
                const lastChar = (lines.length > i + 1) ? '\n' : '';
                if (i === 0) {
                  lastChunkText.text += line + lastChar;
                } else if (line.length > 0) {
                  chunkItems.push({
                    type: 'text',
                    text: line + lastChar,
                  });
                }
              });
            } else {
              // uncompleted first chunk
              lastChunkText.text += x;
            }
          } else {
            // completed first chunk
            chunkItems.push({
              type: 'text',
              text: x,
            });
          }
        } else {
          // new text chunk
          chunkItems.push({
            type: 'text',
            text: x,
          });
        }
      }
    });
    return chunkItems;
  }

  static decodeThread(thread?: LlmThread): LlmDecodedThread | undefined {
    // console.log('thread', thread);
    const decoder = new MessageDecoder();
    return thread ? {
      messages: decoder.decodeMessages(thread.messages),
      threadId: thread.threadId,
    } : undefined;
  }

}

export class MessageService extends MessageDecoder {

  options: MessageServiceOptions;

  constructor(options: MessageServiceOptions) {
    super();
    this.options = options;
  }

  get origin(): string {
    return this.options.endpoint || window.location.origin;
  }

  get locale(): string {
    return this.options.locale || '';
  }

  get url(): string {
    return `${this.origin}/api/llm/message?locale=${this.locale}`;
  }

  get appKey(): string {
    return this.options.appKey || '';
  }

  get apiKey(): string {
    return this.options.apiKey || '';
  }

  get test(): LlmTest {
    return this.options.test || false;
  }

  private lastMessage_: {
    aborter: AbortController;
    onEnd?: (response: StreamResponse) => void;
    decodedChunks: LlmChunk[];
    reader?: ReadableStreamDefaultReader<Uint8Array>;
  } | undefined;

  async addUserMessage(messages: LlmDecodedMessage[], prompt: string, uploaded?: LlmUploaded): Promise<LlmDecodedMessage[]> {
    if (this.test) {
      prompt = getMockPrompt(messages.length, this.locale, this.test) || prompt;
    }
    if (uploaded) {
      const chunkItems = [{
        type: 'text' as const,
        text: prompt,
      }, {
        type: uploaded.type,
        url: uploaded.url,
      }] as LlmChunkItem[];
      const userMessage = {
        chunks: chunkItems,
        content: chunkItems,
        role: 'user',
      };
      return [...messages, userMessage];
    } else {
      const userMessage = {
        chunks: [{
          type: 'text' as const,
          text: prompt,
        }],
        content: prompt,
        role: 'user',
      };
      return [...messages, userMessage];
    }
  }

  async getRequest(messages: LlmDecodedMessage[], state: LlmState): Promise<StreamRequest> {
    return {
      appKey: this.appKey,
      apiKey: this.apiKey,
      messages: messages.filter(x => {
        const chunks = x.chunks
          .filter(x => (
            typeof x.type === 'string' &&
            !['header', 'info', 'end', 'formRequest', 'formRecap', 'formRecapSuccess', 'formRecapError'].includes(x.type)
          ));
        return chunks.length > 0;
      }).map(x => ({ role: x.role, content: x.content })),
      threadId: state.threadId,
      systemContext: state.systemContext,
    };
  }

  async sendMessage(
    request: MessageServiceRequest,
    onMessage?: (response: StreamResponse) => void,
    onEnd?: (response: StreamResponse) => void,
    onChunk?: (chunks: LlmChunk[]) => void
  ) {
    if (this.test) {
      return this.mockMessage(request, onMessage, onEnd, onChunk);
    }
    this.abort();
    // console.log('MessageServiceService.request', request);
    const aborter = new AbortController();
    const decodedChunks: LlmChunk[] = [];
    this.lastMessage_ = {
      aborter,
      decodedChunks,
      onEnd,
    };
    const signal = aborter.signal;
    const url = this.url;
    let bytes = 0;
    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(request),
        headers: {
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (response.body) {
        // console.log('headers', Object.fromEntries(response.headers));
        const threadId = response.headers.get('x-thread-id') || '';
        const messageId = response.headers.get('x-msg-id') || '';
        const header: LlmChunkHeader = {
          type: 'header',
          threadId,
          messageId,
        };
        const headerChunks: LlmChunk[] = [header];
        this.logChunks(headerChunks);
        decodedChunks.push(...headerChunks);
        if (typeof onChunk === 'function') {
          onChunk(headerChunks);
        }
        const reader = response.body.getReader();
        this.lastMessage_.reader = reader;
        this.lastUnparsedText = '';
        // console.log('lastUnparsedText.clear');
        for await (const chunk of readChunks(reader)) {
          if (!signal.aborted) {
            bytes += chunk.length;
            const chunks = this.bytesToChunks(chunk);
            this.logChunks(chunks);
            decodedChunks.push(...chunks);
            const response = this.chunksToResponse(decodedChunks);
            if (typeof onMessage === 'function') {
              onMessage(response);
            }
            if (typeof onChunk === 'function') {
              onChunk(chunks);
            }
          }
        }
        if (!signal.aborted) {
          if (typeof onEnd === 'function') {
            const response = this.chunksToResponse(decodedChunks);
            onEnd(response);
          }
        }
      }
    } catch (error) {
      console.warn('MessageService.sendMessage.error', error);
      if (error === 'cancel') {
        if (typeof onEnd === 'function') {
          const response = this.chunksToResponse(decodedChunks);
          response.threadId = response.threadId || request.threadId || 'unknownThreadId';
          onEnd(response);
        }
        return;
      }
      const errorMessage = typeof error === 'string' ? error : JSON.stringify(error, null, 2);
      /*
      if (error instanceof TypeError) {
        errorMessage = 'TypeError: Browser may not support async iteration';
      }
      */
      if (typeof onEnd === 'function') {
        const errorChunk: LlmChunkError = {
          type: 'error',
          error: errorMessage,
        };
        decodedChunks.push(errorChunk);
        const response = this.chunksToResponse(decodedChunks);
        onEnd(response);
      }
      // throw (error);
    }
  }

  async mockMessage(
    request: MessageServiceRequest,
    onMessage?: (response: StreamResponse) => void,
    onEnd?: (response: StreamResponse) => void,
    onChunk?: (chunks: LlmChunk[]) => void
  ) {
    // console.log('mockMessage', request);
    this.abort();
    const aborter = new AbortController();
    const { messages } = request;
    if (!messages || messages.length === 0) {
      throw { status: 400, message: 'Bad Request: messages is missing' };
    }
    let threadId = request.threadId;
    // threadId is required when messages.length > 1
    if (!threadId && messages.length > 1) {
      throw { status: 400, message: 'Bad Request: threadId is missing' };
    }
    if (!threadId) {
      threadId = uuid();
    }
    const messageId = uuid();
    const decodedChunks: LlmChunk[] = [];
    const header: LlmChunkHeader = { type: 'header', threadId, messageId };
    const headerChunks: LlmChunk[] = [header];
    this.logChunks(headerChunks);
    decodedChunks.push(...headerChunks);
    if (typeof onChunk === 'function') {
      onChunk(headerChunks);
    }
    this.lastMessage_ = {
      aborter,
      decodedChunks,
      onEnd,
    };
    let bytes = 0;
    const signal = aborter.signal;
    const chunks = getMockChunks(request.messages.length, this.locale, this.test);
    chunks.unshift({ type: 'info', threadId });
    chunks.push({ type: 'end' });
    try {
      let controller_: ReadableStreamDefaultController<unknown>;
      const send = async function () {
        const encoder = new TextEncoder();
        await new Promise(x => setTimeout(x, 1000));
        for (const chunk of chunks) {
          if (!signal.aborted) {
            const array = encoder.encode(`${JSON.stringify(chunk)},`);
            controller_.enqueue(array);
            await new Promise(x => setTimeout(x, typeof chunk === 'string' ? 40 : 500));
          }
        }
        if (!signal.aborted) {
          controller_.close();
        }
      };
      const readableStream = new ReadableStream({
        start(controller) {
          controller_ = controller;
          send();
        },
        pull(controller) {
          // We don't really need a pull in this example
        },
        cancel() {
          aborter.abort;
        },
      });
      const reader = readableStream.getReader();
      this.lastMessage_.reader = reader;
      this.lastUnparsedText = '';
      // console.log('lastUnparsedText.clear');
      for await (const chunk of readChunks(reader)) {
        if (!signal.aborted) {
          bytes += chunk.length;
          const chunks = this.bytesToChunks(chunk);
          this.logChunks(chunks);
          decodedChunks.push(...chunks);
          const response = this.chunksToResponse(decodedChunks);
          if (typeof onMessage === 'function') {
            onMessage(response);
          }
          if (typeof onChunk === 'function') {
            onChunk(chunks);
          }
        }
      }
      if (!signal.aborted) {
        if (typeof onEnd === 'function') {
          const response = this.chunksToResponse(decodedChunks);
          onEnd(response);
        }
      }
    } catch (error) {
      console.error('MessageService.mockMessage.error', error);
      const errorMessage = typeof error === 'string' ? error : JSON.stringify(error, null, 2);
      /*
      if (error instanceof TypeError) {
        errorMessage = 'TypeError: Browser may not support async iteration';
      }
      */
      if (typeof onEnd === 'function') {
        const errorChunk: LlmChunkError = {
          type: 'error',
          error: errorMessage,
        };
        decodedChunks.push(errorChunk);
        const response = this.chunksToResponse(decodedChunks);
        onEnd(response);
      }
      // throw (error);
    }
  }

  abort() {
    const lastMessage = this.lastMessage_;
    if (lastMessage && lastMessage.aborter && !lastMessage.aborter.signal.aborted) {
      try {
        if (lastMessage.reader) {
          lastMessage.reader.cancel().catch((error) => {
            console.log('MessageService.abort.reader.error', error);
          });
        }
        lastMessage.aborter.abort('cancel');
      } catch (error) {
        console.log('MessageService.abort.error', error);
      }
      if (typeof lastMessage.onEnd === 'function') {
        const response = this.chunksToResponse(lastMessage.decodedChunks);
        lastMessage.onEnd(response);
      }
    }
  }

  shouldAddFormRequest(messages: LlmDecodedMessage[]): boolean {
    const isTestThread = this.test && getMockPrompt(messages.length, this.locale, this.test) !== undefined;
    const hasFormRequest = messages.find(x => x.chunks.find(x => x.type === 'formRequest') !== undefined);
    const importantMessages = messages.filter(x => x.chunks.find(x => {
      const has = typeof x.type === 'string' &&
        ['event', 'eventItem', 'eventGroup', 'poi', 'poiItem', 'poiGroup', 'tripadvisor', 'tripadvisorItem', 'tripadvisorGroup'].includes(x.type);
      return has;
    }));
    return !isTestThread && !hasFormRequest && importantMessages.length > 1;
  }

}

function readChunks<T>(reader: ReadableStreamDefaultReader<T>) {
  return {
    async*[Symbol.asyncIterator]() {
      let readResult = await reader.read();
      while (!readResult.done) {
        yield readResult.value;
        readResult = await reader.read();
      }
    },
  };
}
